import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { GameState } from '../systems/GameState';
import { OutfitRenderer } from '../systems/OutfitRenderer';
import { OUTFITS_BY_ID } from '../data/outfits';
import { PETS_BY_ID } from '../data/pets';
import { PetRenderer, PET_SHEET_KEY } from '../systems/PetRenderer';
import { svgStringToTexture } from '../utils/svgToTexture';
import type { CharacterExpression } from '../systems/OutfitRenderer';
import { Button } from '../ui/Button';
import { EventBus } from '../systems/EventBus';
import { AudioManager } from '../systems/AudioManager';
import { CharacterAnimator } from '../systems/CharacterAnimator';
import { QuestEngine } from '../systems/QuestEngine';
import { DAILY_POOL } from '../data/quests';

export class HomeScene extends Phaser.Scene {
  private characterSprite: Phaser.GameObjects.Image | null = null;
  private petSprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite | null = null;
  private outfitEquippedHandler = (): void => { void this.refreshCharacter(); };
  private petWanderTimer: Phaser.Time.TimerEvent | null = null;
  private petJumpTimer: Phaser.Time.TimerEvent | null = null;
  private petParticleTimer: Phaser.Time.TimerEvent | null = null;
  private currentExpression: CharacterExpression = 'normal';

  constructor() { super('HomeScene'); }

  async create(): Promise<void> {
    AudioManager.setScene(this);
    AudioManager.playBgm('home');
    this.scene.launch('HUDScene');
    this.cameras.main.setBackgroundColor('#fef0f5');
    if (this.textures.exists('bg_home')) {
      const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_home');
      // 화면을 채우도록 스케일 — cover 방식
      const sx = GAME_WIDTH / bg.width;
      const sy = GAME_HEIGHT / bg.height;
      const s = Math.max(sx, sy);
      bg.setScale(s).setDepth(-100);
      // 가독성용 살짝 톤 다운
      this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xfef0f5, 0.55).setDepth(-99);
    }
    this.cameras.main.fadeIn(300);

    // Daily quest refresh: if last login was a different day, refresh daily quests
    const today = new Date().toDateString();
    const lastLogin = new Date(GameState.data.stats.lastLoginAt).toDateString();
    if (today !== lastLogin) {
      QuestEngine.refreshDailyQuests();
      GameState.data.stats.lastLoginAt = Date.now();
      GameState.data.stats.daysPlayed = (GameState.data.stats.daysPlayed || 0) + 1;
      GameState.persist();
    }

    // 진입할 때마다 stat 기반 퀘스트 동기화 — 다른 씬에서 진행한 결과가 반영되도록
    QuestEngine.syncFromStats();

    // Parallax star background
    this._buildParallaxBg();

    // Stage floor — rounded card style
    const floor = this.add.graphics();
    floor.fillStyle(0xd4a06a, 1);
    floor.fillRoundedRect(0, GAME_HEIGHT - 200, GAME_WIDTH, 200, 0);

    // Subtle floor highlight line
    const floorHighlight = this.add.graphics();
    floorHighlight.fillStyle(0xe8b882, 0.5);
    floorHighlight.fillRect(0, GAME_HEIGHT - 200, GAME_WIDTH, 6);

    // 캐릭터 렌더는 비동기 fire-and-forget — UI 그리기를 막지 않음
    void this.refreshCharacter().catch(err => {
      console.error('[HomeScene] refreshCharacter failed', err);
    });

    // Menu buttons — 화면 중앙 정렬
    const menuY = GAME_HEIGHT - 50;
    const menuItems: { label: string; key: string }[] = [
      { label: '👗 옷장', key: 'ClosetScene' },
      { label: '🎮 미니게임', key: 'MiniGameHubScene' },
      { label: '🛒 상점', key: 'ShopScene' },
      { label: '💃 라이벌', key: 'RunwayScene' },
      { label: '🐾 펫룸', key: 'PetRoomScene' },
      { label: '📚 도감', key: 'CollectionScene' },
    ];
    const btnW = 200;
    const btnGap = 16;
    const totalW = menuItems.length * btnW + (menuItems.length - 1) * btnGap;
    const startX = (GAME_WIDTH - totalW) / 2 + btnW / 2;
    menuItems.forEach((m, i) => {
      new Button(this, startX + i * (btnW + btnGap), menuY, m.label, () => {
        this.cameras.main.fadeOut(200);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.stop('HUDScene');
          this.scene.start(m.key);
        });
      }, { width: btnW, height: 56, style: 'primary' });
    });

    // 일일 미션 카드 (우상단)
    this._renderDailyMission();

    // Name tag — rounded card
    const tagBg = this.add.graphics();
    tagBg.fillStyle(0xffffff, 0.92);
    tagBg.fillRoundedRect(24, 80, 200, 44, 22);
    tagBg.lineStyle(1.5, 0xffd6e6, 1);
    tagBg.strokeRoundedRect(24, 80, 200, 44, 22);

    this.add.text(40, 102, `${GameState.data.profile.name}`, {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '18px',
      fontStyle: '800',
      color: '#3a2540',
    }).setOrigin(0, 0.5);

    this.add.text(40 + this.add.text(0, 0, GameState.data.profile.name, { fontSize: '18px', fontStyle: '800' }).width + 8, 102,
      `Lv.${GameState.data.profile.level}`, {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '16px',
      fontStyle: '700',
      color: '#9b6bff',
    }).setOrigin(0, 0.5);

    const outfitSparkleHandler = (): void => {
      // ✨ sparkle when outfit changes in home scene
      if (!this.characterSprite) return;
      const cx = GAME_WIDTH / 2;
      const cy = GAME_HEIGHT / 2 + 30;
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const sp = this.add.text(
          cx + Math.cos(angle) * 100, cy + Math.sin(angle) * 100,
          '✨', { fontSize: '20px' },
        ).setOrigin(0.5).setDepth(400);
        this.tweens.add({
          targets: sp, alpha: 0, scale: 0.3, duration: 700, delay: i * 50,
          ease: 'Sine.Out', onComplete: () => sp.destroy(),
        });
      }
    };
    EventBus.on('outfit-equipped', outfitSparkleHandler);
    EventBus.on('outfit-equipped', this.outfitEquippedHandler);
    this.events.once('shutdown', () => {
      EventBus.off('outfit-equipped', outfitSparkleHandler);
      EventBus.off('outfit-equipped', this.outfitEquippedHandler);
      if (this.petWanderTimer) this.petWanderTimer.remove();
      if (this.petJumpTimer) this.petJumpTimer.remove();
      if (this.petParticleTimer) this.petParticleTimer.remove();
    });

    // Character tap reaction
    this._setupCharacterTap();

    // Force HUD to refresh with latest currency (in case sub-scene changed it)
    EventBus.emit('currency-changed', GameState.data.currency);
  }

  private _missionContainer: Phaser.GameObjects.Container | null = null;

  private _renderDailyMission(): void {
    // 기존 카드 제거 (재렌더링 시)
    if (this._missionContainer) { this._missionContainer.destroy(); }
    this._missionContainer = this.add.container(0, 0);

    // 사용자의 실제 활성 daily 퀘스트 (questProgress에 등록된 것) — 없으면 풀에서 첫 3개로 fallback
    const activeIds = new Set(
      GameState.data.questProgress
        .filter(p => p.questId.startsWith('daily_'))
        .map(p => p.questId)
    );
    let todaysMissions = DAILY_POOL.filter(q => activeIds.has(q.id));
    if (todaysMissions.length === 0) {
      todaysMissions = DAILY_POOL.slice(0, 3);
    }
    todaysMissions = todaysMissions.slice(0, 3);

    const cardX = GAME_WIDTH - 280;
    const cardY = 100;
    const cardW = 270;
    const missionH = 68; // per-mission row height — taller for readability
    const cardH = 72 + todaysMissions.length * missionH + 10; // dynamic height

    // 카드 배경
    const bg = this.add.graphics();
    bg.fillStyle(0xffffff, 0.96);
    bg.fillRoundedRect(cardX - cardW / 2, cardY, cardW, cardH, 18);
    bg.lineStyle(3, 0xffd95c, 1);
    bg.strokeRoundedRect(cardX - cardW / 2, cardY, cardW, cardH, 18);
    this._missionContainer.add(bg);

    // 헤더
    const header = this.add.text(cardX, cardY + 28, '🎯 오늘의 미션', {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '18px',
      fontStyle: '900',
      color: '#f0a020',
    }).setOrigin(0.5);
    this._missionContainer.add(header);

    const sub = this.add.text(cardX, cardY + 52, '완료하면 별과 코인을 받아요!', {
      fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
      fontSize: '11px',
      color: '#7a5b80',
    }).setOrigin(0.5);
    this._missionContainer.add(sub);

    // 미션 리스트
    todaysMissions.forEach((m, i) => {
      const y = cardY + 76 + i * missionH;
      const progress = GameState.data.questProgress.find(p => p.questId === m.id);
      const cur = progress?.current ?? 0;
      const target = m.goal.target;
      const isComplete = cur >= target;
      const claimed = progress?.claimed ?? false;

      // 미션 배경
      const missionBg = this.add.graphics();
      missionBg.fillStyle(claimed ? 0xeeeeee : isComplete ? 0xfff8d8 : 0xfff5f8, 1);
      missionBg.fillRoundedRect(cardX - cardW / 2 + 8, y - 4, cardW - 16, missionH - 8, 12);
      this._missionContainer!.add(missionBg);

      // 상태 아이콘
      const statusEmoji = claimed ? '✅' : isComplete ? '🌟' : '🎯';
      const statusText = this.add.text(cardX - cardW / 2 + 16, y + 12, statusEmoji, { fontSize: '16px' }).setOrigin(0, 0.5);
      this._missionContainer!.add(statusText);

      // 제목
      const titleStr = m.title.length > 14 ? m.title.slice(0, 13) + '…' : m.title;
      const titleText = this.add.text(cardX - cardW / 2 + 38, y + 4, titleStr, {
        fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
        fontSize: '13px',
        fontStyle: '800',
        color: claimed ? '#aaaaaa' : '#3a2540',
      });
      this._missionContainer!.add(titleText);

      // 진행도 + 보상
      const rewardStr = `${m.reward.stars ? `⭐${m.reward.stars}` : ''} ${m.reward.coins ? `💰${m.reward.coins}` : ''}`.trim();
      const progText = this.add.text(cardX - cardW / 2 + 38, y + 22, `${cur}/${target} · ${rewardStr}`, {
        fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
        fontSize: '11px',
        fontStyle: '700',
        color: claimed ? '#aaaaaa' : isComplete ? '#22cc66' : '#7a5b80',
      });
      this._missionContainer!.add(progText);

      // 진행 바
      const barW = cardW - 48;
      const barX = cardX - cardW / 2 + 24;
      const barY = y + 38;
      const barBg = this.add.graphics();
      barBg.fillStyle(0xdddddd, 1);
      barBg.fillRoundedRect(barX, barY, barW, 6, 3);
      const fillW = Math.min(1, cur / target) * barW;
      const barFill = this.add.graphics();
      barFill.fillStyle(claimed ? 0x999999 : isComplete ? 0xffd95c : 0xff6fa1, 1);
      if (fillW > 0) barFill.fillRoundedRect(barX, barY, fillW, 6, 3);
      this._missionContainer!.add([barBg, barFill]);

      // 받기 버튼 — 완료했고 미수령일 때만
      if (isComplete && !claimed) {
        const btnX = cardX + cardW / 2 - 36;
        const btnY = y + 14;
        const btnBg = this.add.graphics();
        btnBg.fillStyle(0xffd95c, 1);
        btnBg.fillRoundedRect(btnX - 30, btnY - 14, 60, 28, 8);
        btnBg.lineStyle(2, 0xe0a020, 1);
        btnBg.strokeRoundedRect(btnX - 30, btnY - 14, 60, 28, 8);
        const btnTxt = this.add.text(btnX, btnY, '✨받기', {
          fontFamily: 'Pretendard Variable, Pretendard, sans-serif',
          fontSize: '12px',
          fontStyle: '900',
          color: '#5a3000',
        }).setOrigin(0.5);
        const btnHit = this.add.rectangle(btnX, btnY, 60, 28, 0, 0).setInteractive({ useHandCursor: true });
        btnHit.on('pointerdown', () => {
          if (QuestEngine.claim(m.id)) {
            AudioManager.playSfx('star');
            // 펄스 후 다시 그림
            this._renderDailyMission();
          }
        });
        // pulse 애니메이션
        this.tweens.add({ targets: btnTxt, scaleX: 1.1, scaleY: 1.1, duration: 500, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
        this._missionContainer!.add([btnBg, btnTxt, btnHit]);
      }
    });
  }

  private _buildParallaxBg(): void {
    // Layer 1: slow large stars
    const BIG_STARS = 18;
    for (let i = 0; i < BIG_STARS; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * (GAME_HEIGHT - 200);
      const size = 3 + Math.random() * 4;
      const star = this.add.text(x, y, '✦', {
        fontSize: `${size * 4}px`,
        color: '#ff6fa1',
        alpha: 0.18 + Math.random() * 0.12,
      } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setAlpha(0.18 + Math.random() * 0.12);
      const speed = 0.03 + Math.random() * 0.04;
      this.tweens.add({
        targets: star,
        y: y - 30 - Math.random() * 40,
        duration: (8000 + Math.random() * 8000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.InOut',
        delay: Math.random() * 5000,
      });
      this.tweens.add({
        targets: star,
        alpha: 0.05,
        duration: (3000 + Math.random() * 4000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.InOut',
        delay: Math.random() * 3000,
      });
      void speed;
    }
    // Layer 2: small sparkle dots
    const DOTS = 30;
    for (let i = 0; i < DOTS; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * (GAME_HEIGHT - 200);
      const dot = this.add.circle(x, y, 2 + Math.random() * 2, 0xffd6e6, 0.3 + Math.random() * 0.2);
      this.tweens.add({
        targets: dot,
        alpha: 0,
        duration: 1500 + Math.random() * 2500,
        yoyo: true,
        repeat: -1,
        delay: Math.random() * 2000,
      });
    }
  }

  private _setupCharacterTap(): void {
    // Invisible hit zone over character
    const hitZone = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30, 240, 480, 0x000000, 0);
    hitZone.setInteractive({ useHandCursor: true });
    hitZone.on('pointerdown', () => this._onCharacterTap());
  }

  private _onCharacterTap(): void {
    if (!this.characterSprite) return;

    // Switch to happy expression temporarily
    this.currentExpression = 'happy';
    void this.refreshCharacter();

    // Happy bounce
    this.tweens.add({
      targets: this.characterSprite,
      scaleX: 5.3, scaleY: 5.3,
      duration: 120, yoyo: true, ease: 'Back.Out',
    });
    // Sparkle/heart particles
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2 - 60;
    const emojis = ['❤️', '✨', '💕', '⭐', '💖'];
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const e = this.add.text(
        cx + Math.cos(angle) * 80,
        cy + Math.sin(angle) * 60,
        emojis[i % emojis.length],
        { fontSize: '24px' },
      ).setOrigin(0.5).setDepth(400);
      this.tweens.add({
        targets: e, y: e.y - 80, alpha: 0, scale: 1.5,
        duration: 900, delay: i * 60, ease: 'Sine.Out',
        onComplete: () => e.destroy(),
      });
    }
    // Sound
    AudioManager.playSfx('pet_happy');

    // Revert to normal after 1s
    this.time.delayedCall(1000, () => {
      this.currentExpression = 'normal';
      void this.refreshCharacter();
    });
  }

  private _startPetBehavior(species?: import('../types').PetSpecies): void {
    if (!this.petSprite) return;
    // Clear previous timers
    if (this.petWanderTimer) this.petWanderTimer.remove();
    if (this.petJumpTimer) this.petJumpTimer.remove();
    if (this.petParticleTimer) this.petParticleTimer.remove();

    const pet = this.petSprite;
    const baseX = GAME_WIDTH / 2 + 220;
    const baseY = GAME_HEIGHT / 2 + 180;
    const isAnim = species !== undefined
      && PetRenderer.hasSheet(species)
      && pet instanceof Phaser.GameObjects.Sprite;
    const idleAnim = species ? PetRenderer.getAnimKey(species, 'idle') : '';
    const walkAnim = species ? PetRenderer.getAnimKey(species, 'walk') : '';
    const cheerAnim = species ? PetRenderer.getAnimKey(species, 'cheer') : '';

    // Wander: every 5-7s move to a new nearby position
    const wander = (): void => {
      if (!pet.active) return;
      const targetX = baseX + (Math.random() - 0.5) * 300;
      const targetY = baseY + (Math.random() - 0.5) * 80;
      const sp = pet as Phaser.GameObjects.Sprite;
      // 진행 방향에 맞춰 좌우 반전
      if (isAnim) {
        sp.setFlipX(targetX < pet.x);
        if (walkAnim && this.anims.exists(walkAnim)) sp.play(walkAnim, true);
      }
      this.tweens.add({
        targets: pet, x: targetX, y: targetY,
        duration: 1200, ease: 'Sine.InOut',
        onComplete: () => {
          if (!pet.active) return;
          if (isAnim && idleAnim && this.anims.exists(idleAnim)) sp.play(idleAnim, true);
        },
      });
    };

    this.petWanderTimer = this.time.addEvent({
      delay: 5000 + Math.random() * 2000,
      loop: true,
      callback: wander,
    });

    // Idle breath (gentle bob)
    this.tweens.add({
      targets: pet, y: pet.y - 6,
      duration: 900, yoyo: true, repeat: -1, ease: 'Sine.InOut',
    });

    // Jump: random 2-5s — sheet 펫은 cheer 애니메이션 짧게 재생
    this.petJumpTimer = this.time.addEvent({
      delay: 2000 + Math.random() * 3000,
      loop: true,
      callback: () => {
        if (!pet.active) return;
        const savedY = pet.y;
        const sp = pet as Phaser.GameObjects.Sprite;
        if (isAnim && cheerAnim && this.anims.exists(cheerAnim)) {
          sp.play(cheerAnim, true);
        }
        this.tweens.add({
          targets: pet, y: pet.y - 40,
          duration: 200, yoyo: true, ease: 'Sine.Out',
          onComplete: () => {
            pet.y = savedY;
            if (isAnim && idleAnim && this.anims.exists(idleAnim)) sp.play(idleAnim, true);
          },
        });
      },
    });

    // Particle: ❤️/✨ popup near pet
    this.petParticleTimer = this.time.addEvent({
      delay: 3000 + Math.random() * 2000,
      loop: true,
      callback: () => {
        if (!pet.active) return;
        const icon = Math.random() < 0.5 ? '❤️' : '✨';
        const sp = this.add.text(pet.x + (Math.random() - 0.5) * 60, pet.y - 40, icon, { fontSize: '22px' })
          .setOrigin(0.5).setDepth(300).setAlpha(0.9);
        this.tweens.add({
          targets: sp, y: sp.y - 50, alpha: 0, duration: 800, ease: 'Sine.Out',
          onComplete: () => sp.destroy(),
        });
      },
    });
  }

  async refreshCharacter(): Promise<void> {
    const equipped = OutfitRenderer.resolveEquipped(GameState.data.currentEquipped, OUTFITS_BY_ID);
    // 빡빡이 베이스 + 헤어 오버레이 합성 (Phaser Image 합성, SVG 우회)
    if (this.characterSprite) { CharacterAnimator.reset(this, this.characterSprite); this.characterSprite.destroy(); }

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2 + 30;
    const hairId = equipped.hair?.id;
    const fullKey = hairId ? `char_full_${hairId}` : null;

    if (fullKey && this.textures.exists(fullKey)) {
      this.characterSprite = this.add.image(cx, cy, fullKey);
      this.characterSprite.setScale(1.25);
    } else if (this.textures.exists('character_bald')) {
      this.characterSprite = this.add.image(cx, cy, 'character_bald');
      this.characterSprite.setScale(1.25);
    } else {
      const svg = OutfitRenderer.renderCharacterSVG(equipped, 0, this.currentExpression);
      const k = 'home_character_main';
      await svgStringToTexture(this, k, svg);
      this.characterSprite = this.add.image(cx, cy, k);
      this.characterSprite.setScale(1.25);
    }
    CharacterAnimator.idle(this, this.characterSprite);

    // 활성 펫 표시 — 캐릭터 옆, 발 근처
    if (this.petSprite) {
      this.petSprite.destroy();
      this.petSprite = null;
    }
    const activeId = GameState.data.activePetId;
    if (activeId) {
      const pet = GameState.data.ownedPets.find(p => p.petId === activeId);
      if (pet) {
        const meta = PETS_BY_ID[pet.petId];
        if (meta) {
          const px = GAME_WIDTH / 2 + 220;
          const py = GAME_HEIGHT / 2 + 180;
          // 시트 애니메이션 우선 (dog/cat/rabbit/hamster)
          if (PetRenderer.hasSheet(meta.species) && this.textures.exists(PET_SHEET_KEY)) {
            const sprite = this.add.sprite(px, py, PET_SHEET_KEY,
              PetRenderer.getSheetFrame(meta.species, 'idle'));
            sprite.setScale(1.4);
            const idleKey = PetRenderer.getAnimKey(meta.species, 'idle');
            if (this.anims.exists(idleKey)) sprite.play(idleKey);
            this.petSprite = sprite;
          } else {
            // 정적 PNG → SVG fallback
            const spriteKey = PetRenderer.getSpriteKey(meta.species);
            let useKey: string;
            let scale: number;
            if (this.textures.exists(spriteKey)) {
              useKey = spriteKey;
              scale = 0.49;
            } else {
              const petSvg = PetRenderer.renderSVG(meta.species);
              useKey = `home_pet_${Date.now()}`;
              await svgStringToTexture(this, useKey, petSvg);
              scale = 2.1;
            }
            this.petSprite = this.add.image(px, py, useKey).setScale(scale);
          }

          // Start pet wander behavior
          this._startPetBehavior(meta.species);
        }
      }
    }
  }
}
